<script setup lang="ts">
import { KButton, KCheckBox, KTextInput } from "@kaleidoscope-solutions/ks-design-system"
import { onClickOutside } from "@vueuse/core"
import { computed, onMounted, ref } from "vue"

const props = defineProps<{
  values: string[]
  checked: string[]
  anchorEl: HTMLElement | null
}>()

const emit = defineEmits<{
  (event: "update:checked", value: string[]): void
  (event: "close"): void
  (event: "clear"): void
}>()

const search = ref("")
const menuRef = ref<HTMLElement>()
const menuStyle = ref<{ top: string; left: string } | null>(null)

onClickOutside(menuRef, () => emit("close"))

const filteredValues = computed(() => {
  if (!search.value) {
    return props.values
  }
  return props.values.filter((v) => v.toLowerCase().includes(search.value.toLowerCase()))
})

function isChecked(value: string) {
  return props.checked.includes(value)
}

function toggleValue(value: string) {
  const next = isChecked(value)
    ? props.checked.filter((v) => v !== value)
    : [...props.checked, value]
  emit("update:checked", next)
}

onMounted(() => {
  if (props.anchorEl) {
    const rect = props.anchorEl.getBoundingClientRect()
    // Position menu at the bottom right of the anchor, fixed at mount
    menuStyle.value = {
      top: `${rect.bottom + window.scrollY}px`,
      left: `${rect.right + window.scrollX}px` // 200px is min width, adjust as needed
    }
  }
})
</script>

<template>
  <teleport to="body">
    <div
      ref="menuRef"
      class="absolute z-30 min-w-[200px] rounded-lg border border-gray-200 bg-white shadow-lg"
      :style="menuStyle"
      @click.stop
    >
      <div class="p-2">
        <KTextInput
          v-model="search"
          placeholder="Search..."
          class="mb-2 w-full"
        />
        <div class="max-h-48 overflow-auto pr-1">
          <div
            v-for="value in filteredValues"
            :key="value"
            class="mb-1 flex items-center gap-2"
          >
            <KCheckBox
              :id="`filter-checkbox-${value}`"
              :model-value="isChecked(value)"
              :label="value === '' ? '(Blank)' : value"
              @update:model-value="toggleValue(value)"
            />
            <span>{{ value === '' ? '(Blank)' : value }}</span>
          </div>
        </div>
        <div class="mt-2 flex justify-between gap-2">
          <KButton
            variety="secondary"
            size="md"
            @click="$emit('clear')"
          >
            Clear
          </KButton>
          <KButton
            variety="primary"
            size="md"
            @click="$emit('close')"
          >
            Done
          </KButton>
        </div>
      </div>
    </div>
  </teleport>
</template>
